/**
 * WizardPage.jsx
 * ──────────────
 * Public 4-step guided search: Actor → Crop → Objective → Complexity
 * No login required. Results displayed inline after step 4.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWizardOptions, searchSchemes } from "../../services/api";
import SchemeCard from "../../components/SchemeCard";


// const STEPS = [
//   { key: "actor_id",     label: "Actor",      title: "Who are you?",             sub: "Select the role that best describes you." },
//   { key: "crop_id",      label: "Crop",        title: "Which crop?",              sub: "Select the crop you are working with." },
//   { key: "objective_id", label: "Objective",   title: "What is your objective?",  sub: "What outcome are you working towards?" },
//   { key: "complexity",   label: "Intensity",  title: "Level of intensity?",         sub: "Select the management intensity level of the scheme." },
// ];
const STEPS = [
  { key: "actor_id",     label: "Actor",      title: "Who are you?",                                          sub: "Farmer or nursery operator" },
  { key: "crop_id",      label: "Crop",        title: "Which crop are you working with?",                     sub: "Almond, grapevine, or olive" },
  { key: "objective_id", label: "Objective",   title: "What is your management objective?",                   sub: "Managing an existing crop or establishing/replacing a plantation?" },
  { key: "complexity",   label: "Intensity",  title: "What level of management effort can you implement?",    sub: "Low, medium, or high intensity" },
];

const OPTS_KEY = {
  actor_id:     "actors",
  crop_id:      "crops",
  objective_id: "objectives",
  complexity:   "complexity",
};

/* ── OLD STEP_ICONS (generic icons for all steps) — kept for reference ────────

const STEP_ICONS = {
  actor_id: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  crop_id: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22V12M12 12C12 7 17 3 17 3s-2 9-5 9zM12 12C12 7 7 3 7 3s2 9 5 9z"/>
      <path d="M5 22h14"/>
    </svg>
  ),
  objective_id: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  complexity: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="14" width="4" height="8"/><rect x="9" y="9" width="4" height="13"/>
      <rect x="16" y="4" width="4" height="18"/>
    </svg>
  ),
};
── END OLD STEP_ICONS ── */


// ── Images for actor & crop option cards ──────────────────────────────────────
// Place these files in frontend/public/:
//   farmer.png  nursery.jpg  almond.png  grapevine.png  olive.png
const OPTION_IMAGES = {
  "farmers":        "/farmer.png",
  "farmer":         "/farmer.png",
  "nurseries":      "/nursery.png",
  "nursery worker": "/nursery.png",
  "almond":         "/almond.png",
  "grapevine":      "/grapevine.png",
  "olive":          "/olive.png",
};

// Fuzzy lookup: tries exact match first, then first-word stem match.
// Handles DB names like "Nurseries" matching key "nursery worker",
// or "Farmers" matching key "farmer".
function findOptionImage(name) {
  if (!name) return null;
  const lower = name.toLowerCase().trim();
  if (OPTION_IMAGES[lower]) return OPTION_IMAGES[lower];
  const nameFirst = lower.split(" ")[0];   // e.g. "nurseries" from "nurseries of europe"
  for (const [key, src] of Object.entries(OPTION_IMAGES)) {
    const keyFirst = key.split(" ")[0];    // e.g. "nursery" from "nursery worker"
    // match if either stem starts with the other (handles plurals like nursery/nurseries)
    if (nameFirst.startsWith(keyFirst) || keyFirst.startsWith(nameFirst)) return src;
  }
  return null;
}

// ── Objective icons — matched by name keyword ─────────────────────────────────
function getObjectiveIcon(name) {
  const lower = (name || "").toLowerCase();
  // "replace / new plantation / start"
  if (lower.includes("replac") || lower.includes("new") || lower.includes("start") || lower.includes("plant")) {
    return (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22V12"/>
        <path d="M12 12C9 9 5 10 3 7c4 0 7 2 9 5z"/>
        <path d="M12 12c3-3 7-2 9-5-4 0-7 2-9 5z"/>
        <path d="M5 22h14"/>
      </svg>
    );
  }
  // default: manage existing crop (home/farm icon)
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}

// ── Intensity bars: 1 filled = Low, 2 = Medium, 3 = High ─────────────────────
function IntensityBars({ name }) {
  const lower  = (name || "").toLowerCase();
  const filled = lower.includes("high") ? 3 : lower.includes("med") ? 2 : 1;
  const on  = "#4B7518";
  const off = "#d4dbc8";
  return (
    <svg width="38" height="30" viewBox="0 0 32 26" fill="none">
      <rect x="1"  y="18" width="8" height="7" rx="2" fill={filled >= 1 ? on : off} />
      <rect x="12" y="10" width="8" height="15" rx="2" fill={filled >= 2 ? on : off} />
      <rect x="23" y="2"  width="8" height="23" rx="2" fill={filled >= 3 ? on : off} />
    </svg>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ currentStep, done }) {
  const labels = [...STEPS.map(s => s.label), "Results"];
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
      {labels.map((label, i) => {
        const isLast   = i === labels.length - 1;
        const isDone   = done ? i < labels.length : i < currentStep;
        const isActive = done ? i === labels.length - 1 : i === currentStep;
        const cls      = isDone ? "done" : isActive ? "active" : "idle";
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", flex: isLast ? "0 0 auto" : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, minWidth: 52 }}>
              <div className={`progress-dot ${cls}`}>
                {isDone ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                ) : i + 1}
              </div>
              <span className="wiz-progress-label" style={{
                color: isActive ? "#4B7518" : "#8a9a7a",
                fontWeight: isActive ? 700 : 400,
                fontFamily: "'Nunito', sans-serif",
              }}>
                {label}
              </span>
            </div>
            {!isLast && (
              <div style={{
                flex: 1, height: 2, marginBottom: 18, marginLeft: 4, marginRight: 4,
                background: isDone ? "#4B7518" : "#d4dbc8",
                transition: "background .3s",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ── OLD OptionCard (icon-only, all steps same layout) — kept for reference ───
function OptionCard({ id, name, selected, onClick, stepKey }) {
  return (
    <div
      className={`option-card ${selected ? "active" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
    >
      <div className="opt-icon-box">
        {STEP_ICONS[stepKey]}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#5C4B00",
          fontFamily: "'Nunito',sans-serif" }}>
          {name}
        </div>
      </div>
      {selected && (
        <div style={{ color: "#4B7518", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
      )}
    </div>
  );
}
── END OLD OptionCard ── */


// ── Option card ───────────────────────────────────────────────────────────────
// ACTIVE LAYOUT: TOP  — image/icon on top, name bar below (uniform for all steps)
// To switch to LEFT layout: comment out the TOP block and uncomment the LEFT block below.

const checkIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4B7518" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

// shared card wrapper style
const cardWrap = (selected) => ({
  cursor: "pointer", borderRadius: 12, overflow: "hidden",
  border: selected ? "2.5px solid #4B7518" : "1.5px solid #d4dbc8",
  background: "#fff",
  boxShadow: selected
    ? "0 0 0 3px rgba(75,117,24,.15), 0 2px 8px rgba(0,0,0,.08)"
    : "0 1px 4px rgba(0,0,0,.06)",
  transition: "border .15s, box-shadow .15s",
});

// shared name bar
function NameBar({ name, selected }) {
  return (
    <div style={{
      padding: "8px 10px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: selected ? "#f0f7e6" : "#fff",
      borderTop: "1px solid rgba(212,217,128,.4)",
    }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#5C4B00",
        fontFamily: "'Nunito',sans-serif" }}>
        {name}
      </span>
      {selected && checkIcon}
    </div>
  );
}


// ── ACTIVE: TOP layout ─── Images and Icons on Top ─────────────────────────────────────────────────────
// function OptionCard({ id, name, selected, onClick, stepKey }) {
//   const imgSrc = (stepKey === "actor_id" || stepKey === "crop_id")
//     ? findOptionImage(name) : null;
//   const icon = !imgSrc
//     ? (stepKey === "complexity" ? <IntensityBars name={name} /> : getObjectiveIcon(name))
//     : null;

//   return (
//     <div onClick={onClick} role="button" tabIndex={0}
//          onKeyDown={e => e.key === "Enter" && onClick()}
//          style={cardWrap(selected)}>

//       {/* Top area — photo or icon box */}
//       <div style={{
//         width: "100%", height: 115, overflow: "hidden",
//         background: imgSrc ? "transparent" : (selected ? "#dff0c8" : "#E8EDB4"),
//         display: "flex", alignItems: "center", justifyContent: "center",
//         color: "#4B7518",
//       }}>
//         {imgSrc ? (
//           <>
//             <img src={imgSrc} alt={name}
//               style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
//               onError={e => {
//                 e.currentTarget.style.display = "none";
//                 e.currentTarget.nextSibling.style.display = "flex";
//               }}
//             />
//             {/* Fallback if image file missing */}
//             <div style={{
//               display: "none", width: "100%", height: "100%",
//               background: "#E8EDB4", alignItems: "center", justifyContent: "center",
//             }}>
//               <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#4B7518" strokeWidth="1.2">
//                 <rect x="3" y="3" width="18" height="18" rx="2"/>
//                 <circle cx="8.5" cy="8.5" r="1.5"/>
//                 <path d="M21 15l-5-5L5 21"/>
//               </svg>
//             </div>
//           </>
//         ) : icon}
//       </div>

//       <NameBar name={name} selected={selected} />
//     </div>
//   );
// }



//  ── LEFT layout OptionCard — comment out TOP block above and uncomment this to switch ──
function OptionCard({ id, name, selected, onClick, stepKey }) {
  const imgSrc = (stepKey === "actor_id" || stepKey === "crop_id")
    ? findOptionImage(name) : null;
  const icon = !imgSrc
    ? (stepKey === "complexity" ? <IntensityBars name={name} /> : getObjectiveIcon(name))
    : null;

  return (
    <div onClick={onClick} role="button" tabIndex={0}
         onKeyDown={e => e.key === "Enter" && onClick()}
         style={{ ...cardWrap(selected), display: "flex", alignItems: "stretch" }}>

      {/* // Left side — photo or icon box (80px wide) */}
      <div style={{
        width: 80, flexShrink: 0, overflow: "hidden",
        background: imgSrc ? "transparent" : (selected ? "#dff0c8" : "#E8EDB4"),
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#4B7518",
      }}>
        {imgSrc ? (
          <>
            <img src={imgSrc} alt={name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={e => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextSibling.style.display = "flex";
              }}
            />
            <div style={{
              display: "none", width: "100%", height: "100%",
              background: "#E8EDB4", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4B7518" strokeWidth="1.2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
            </div>
          </>
        ) : icon}
      </div>

      {/* // Right side — name + checkmark */}
      <div style={{
        flex: 1, padding: "14px 12px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderLeft: "1px solid rgba(212,217,128,.4)",
        background: selected ? "#f0f7e6" : "#fff",
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#5C4B00",
          fontFamily: "'Nunito',sans-serif" }}>
          {name}
        </span>
        {selected && checkIcon}
      </div>
    </div>
  );
}
// ── END LEFT layout ── */


// ── Selection pills ───────────────────────────────────────────────────────────
// The pills showing "Actor:", "Crop:", "Objective:", "Intensity:"
function SelectionPills({ selections, options }) {
  if (!options) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
      {STEPS.map(({ key, label }) => {
        const opts = options[OPTS_KEY[key]] ?? [];
        const match = opts.find(o => (o.id ?? o) === selections[key]);
        const name  = match?.name ?? selections[key];
        if (!name) return null;
        return (
          <span key={key} style={{
            fontSize: 12, padding: "4px 12px", borderRadius: 20,
            background: "#4B7518", color: "#fff",
            border: "1px solid #3a5c10", fontWeight: 600,
          }}>
            <span style={{ fontWeight: 400, color: "rgba(255,255,255,0.75)" }}>{label}: </span>
            {name}
          </span>
        );
      })}
    </div>
  );
}

// ── Wizard responsive styles ──────────────────────────────────────────────────

function WizardStyles() {
  return (
    <style>{`
      .wiz-nav-inner {
        max-width: 1100px; margin: 0 auto; padding: 0 24px;
        height: 80px; display: flex; align-items: center; justify-content: space-between;
      }
      .wiz-logo { height: 70px; width: 200px; object-fit: contain; }
      .wiz-home-btn {
        background: none; border: 1px solid #d4dbc8; border-radius: 7px;
        padding: 6px 14px; font-size: 16px; color: #775F46; cursor: pointer;
        font-family: 'Nunito', sans-serif; display: flex; align-items: center; gap: 6px;
      }
      .wiz-opts-grid {
        display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 24px;
        justify-content: center;
      }
      .wiz-opts-grid > * {
        flex: 0 0 calc(33.333% - 7px); min-height: 84px; box-sizing: border-box;
      }
      .wiz-opts-grid > *:first-child:nth-last-child(2),
      .wiz-opts-grid > *:first-child:nth-last-child(2) ~ * {
        flex: 0 0 calc(50% - 5px);
      }
      .wiz-progress-label { font-size: 10px; white-space: nowrap; }
      @media (max-width: 640px) {
        .wiz-nav-inner { height: 60px !important; padding: 0 16px !important; }
        .wiz-logo { height: 48px !important; width: 140px !important; }
        .wiz-home-btn { font-size: 13px !important; padding: 6px 10px !important; }
        .wiz-opts-grid > * { flex: 0 0 calc(50% - 5px) !important; }
        .wiz-progress-label { font-size: 9px !important; }
        .wiz-inner { padding: 20px 16px 36px !important; }
      }
      @media (max-width: 400px) {
        .wiz-opts-grid > * { flex: 0 0 100% !important; }
      }
    `}</style>
  );
}

// ── Navbar for search page ────────────────────────────────────────────────────

function SearchNavbar() {
  const navigate = useNavigate();
  return (
    <nav style={{
      background: "#fff", borderBottom: "2px solid #D4D980",
      position: "sticky", top: 0, zIndex: 100,
    }}>
      <div className="wiz-nav-inner">
        <button onClick={() => navigate("/")} style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "none", border: "none", cursor: "pointer", padding: 0,
        }}>
          <img src="/logo.png" alt="BeXyl" className="wiz-logo"
               onError={e => e.target.style.display = "none"} />
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/")} className="wiz-home-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Home
          </button>
        </div>
      </div>
    </nav>
  );
}

// ── Main wizard page ──────────────────────────────────────────────────────────

export default function WizardPage() {
  const [options,    setOptions]    = useState(null);
  const [optErr,     setOptErr]     = useState("");
  const [step,       setStep]       = useState(0);
  const [selections, setSelections] = useState({});
  const [results,    setResults]    = useState(null);
  const [searching,  setSearching]  = useState(false);
  const [searchErr,  setSearchErr]  = useState("");

  useEffect(() => {
    fetchWizardOptions()
      .then(setOptions)
      .catch(e => setOptErr("Could not load options — is the backend running? " + e.message));
  }, []);

  function select(value) {
    const key = STEPS[step].key;
    setSelections(p => ({ ...p, [key]: value }));
  }

  function goNext() {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else runSearch();
  }

  function goBack() {
    if (results) { setResults(null); setStep(STEPS.length - 1); }
    else setStep(s => Math.max(0, s - 1));
  }

  function restart() {
    setStep(0); setSelections({}); setResults(null); setSearchErr("");
  }

  async function runSearch() {
    setSearching(true); setSearchErr("");
    try {
      const data = await searchSchemes(selections);
      setResults(data);
    } catch (err) {
      setSearchErr("Search failed: " + err.message);
    } finally {
      setSearching(false);
    }
  }

  const currentStepMeta = STEPS[step];
  const currentOpts     = options ? (options[OPTS_KEY[currentStepMeta.key]] ?? []) : [];
  const currentSel      = selections[currentStepMeta.key];

  return (
    <div style={{ minHeight: "100vh", background: "#F6F9F2", display: "flex", flexDirection: "column" }}>
      <WizardStyles />
      <SearchNavbar />

      {/* Breadcrumb strip */}
      <div className="no-print" style={{ background: "#4B7518", padding: "10px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px",
          display: "flex", alignItems: "center", gap: 8 }}>
          {/* <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D4D980" strokeWidth="2">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
          </svg> */}
          <span style={{ fontSize: 13, color: "#D4D980", fontWeight: 600,
            fontFamily: "'Nunito',sans-serif" }}>
            Guided IPM search
          </span>
        </div>
      </div>

      <div className="wiz-inner" style={{ maxWidth: results ? 1100 : 720, margin: "0 auto",
        padding: "32px 24px 48px", flex: 1, width: "100%" }}>

        {optErr && (
          <div style={{ background: "#FCEBEB", border: "1px solid #f0a0a0", borderRadius: 10,
            padding: "14px 18px", color: "#A32D2D", fontSize: 13, marginBottom: 24 }}>
            {optErr}
          </div>
        )}

        {!options && !optErr && (
          <div style={{ textAlign: "center", color: "#775F46", fontSize: 14, paddingTop: 60 }}>
            Connecting to backend…
          </div>
        )}

        {options && !results && (
          /* ── Wizard step view ── */
          <div style={{ background: "#fff", border: "1.5px solid #4B7518",
            borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(75,117,24,.08)" }}>

            {/* Wizard header */}
            <div style={{ background: "#F1F1F1", padding: "14px 24px",
              borderBottom: "1px solid #4B7518" }}>
            {/* <div style={{ background: "#F1F1F1", padding: "14px 24px",
              borderBottom: "1px solid rgba(212,217,128,.5)" }}> */}
              <div style={{ fontSize: 14, fontWeight: 700, color: "#5C4B00",
                fontFamily: "'Nunito',sans-serif", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4B7518" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                Guided IPM search
              </div>
              <div style={{ fontSize: 12, color: "#775F46", marginTop: 2 }}>
                Answer four questions to find the most relevant IPM schemes.
              </div>
            </div>

            <div style={{ padding: "24px 24px 20px" }}>
              <ProgressBar currentStep={step} done={false} />

              <div style={{ fontSize: 18, fontWeight: 700, color: "#5C4B00",
                fontFamily: "'Nunito',sans-serif", marginBottom: 4 }}>
                {currentStepMeta.title}
              </div>
              <div style={{ fontSize: 13, color: "#775F46", marginBottom: 18 }}>
                {currentStepMeta.sub}
              </div>

              {currentOpts.length === 0 ? (
                <div style={{ background: "#FAEEDA", border: "1px solid #FAC775",
                  borderRadius: 8, padding: "14px 18px", fontSize: 13, color: "#633806" }}>
                  No options available for this step yet. The administrator may need to upload scheme data first.
                </div>
              ) : (
                <div className="wiz-opts-grid">
                  {currentOpts.map(opt => {
                    const val  = opt.id ?? opt;
                    const name = opt.name ?? opt;
                    return (
                      <OptionCard
                        key={val}
                        id={val}
                        name={name}
                        selected={currentSel === val}
                        onClick={() => select(val)}
                        stepKey={currentStepMeta.key}
                      />
                    );
                  })}
                </div>
              )}

              {searchErr && (
                <div style={{ background: "#FCEBEB", border: "1px solid #f0a0a0",
                  borderRadius: 8, padding: "12px 16px", color: "#A32D2D",
                  fontSize: 13, marginBottom: 16 }}>
                  {searchErr}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                paddingTop: 16, borderTop: "1px solid #e4edd4" }}>
                <button
                  onClick={goBack}
                  style={{
                    visibility: step === 0 ? "hidden" : "visible",
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#fff", border: "1.5px solid #c8d8b0",
                    borderRadius: 8, padding: "9px 20px", fontSize: 14,
                    color: "#775F46", cursor: "pointer", fontFamily: "'Nunito',sans-serif",
                    fontWeight: 600,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                  </svg>
                  Back
                </button>
                <button
                  onClick={goNext}
                  disabled={!currentSel || searching || currentOpts.length === 0}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: (!currentSel || searching) ? "#a8c880" : "#4B7518",
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "10px 26px", fontSize: 14, fontWeight: 700,
                    cursor: (!currentSel || searching) ? "not-allowed" : "pointer",
                    fontFamily: "'Nunito',sans-serif", transition: "background .15s",
                  }}
                >
                  {searching ? (
                    "Searching…"
                  ) : step === STEPS.length - 1 ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
                      </svg>
                      Find IPM schemes
                    </>
                  ) : (
                    <>
                      Next step
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {options && results && (
          /* ── Results view ── */
          <div>
            <div className="no-print"><ProgressBar currentStep={step} done={true} /></div>
            <SelectionPills selections={results.selections} options={options} />

            {/* Print bar */}
            <div className="no-print" style={{
              background: "#fff", border: "1px solid #d4dbc8", borderRadius: 10,
              padding: "12px 18px", marginBottom: 14,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 10,
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#5C4B00",
                  fontFamily: "'Nunito',sans-serif" }}>
                  {results.total} IPM scheme{results.total !== 1 ? "s" : ""} found
                  {/* {results.total > 0 && " - sorted by expert score"} */}
                  {results.total > 0 && ""}
                </div>
                <div style={{ fontSize: 15, color: "#775F46", marginTop: 2 }}>
                  {Object.values(results.selections).filter(Boolean).join(" · ")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => window.print()} style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  background: "#5C4B00", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 18px", fontSize: 13,
                  fontWeight: 600, cursor: "pointer", fontFamily: "'Nunito',sans-serif",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print results
                </button>
                <button onClick={goBack} style={{
                  background: "#fff", border: "1px solid #c8d8b0",
                  borderRadius: 8, padding: "8px 16px", fontSize: 13,
                  color: "#775F46", cursor: "pointer",
                }}>
                  Change answers
                </button>
                <button onClick={restart} style={{
                  background: "#fff", border: "1px solid #c8d8b0",
                  borderRadius: 8, padding: "8px 16px", fontSize: 13,
                  color: "#775F46", cursor: "pointer",
                }}>
                  Start over
                </button>
              </div>
            </div>

            {results.total === 0 ? (
              <div style={{
                background: "#fff", border: "1.5px solid #D4D980", borderRadius: 14,
                padding: "48px 24px", textAlign: "center",
              }}>
                <div style={{ fontSize: 18, color: "#5C4B00", fontWeight: 700,
                  fontFamily: "'Nunito',sans-serif", marginBottom: 8 }}>
                  No IPM schemes found for your selections
                </div>
                {/* <div style={{ fontSize: 14, color: "#775F46" }}>
                  The administrator may not have uploaded data for this combination yet.
                </div> */}
              </div>
            ) : (
              results.schemes.map(scheme => (
                <SchemeCard
                  key={scheme.scheme_id}
                  scheme={scheme}
                  actors={options.actors}
                  objectives={options.objectives}
                  crops={options.crops}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
